import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Clock, CreditCard, Calendar, PieChart } from 'lucide-react';
import financeService from '../../services/financeService';

const FinanceDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await financeService.getFinanceStats();
      setStats(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
        <button 
          onClick={fetchStats}
          className="ml-4 text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(stats?.total_revenue || 0),
      icon: DollarSign,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700'
    },
    {
      title: 'This Month',
      value: formatCurrency(stats?.this_month || 0),
      icon: Calendar,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700'
    },
    {
      title: 'Today Collection',
      value: formatCurrency(stats?.today || 0),
      icon: TrendingUp,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700'
    },
    {
      title: 'Pending Payments',
      value: formatCurrency(stats?.total_pending || 0),
      icon: Clock,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700'
    }
  ];

  const paymentModes = [
    { name: 'Cash', amount: stats?.by_mode?.cash || 0, color: 'bg-green-500' },
    { name: 'UPI', amount: stats?.by_mode?.upi || 0, color: 'bg-blue-500' },
    { name: 'Card', amount: stats?.by_mode?.card || 0, color: 'bg-purple-500' }
  ];

  const totalByMode = paymentModes.reduce((sum, mode) => sum + mode.amount, 0);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`${stat.bgColor} rounded-lg p-6 border border-gray-200`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-bold ${stat.textColor}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment Mode Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center mb-6">
          <PieChart className="w-5 h-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Payment Mode Breakdown</h3>
        </div>

        <div className="space-y-4">
          {paymentModes.map((mode, index) => {
            const percentage = totalByMode > 0 ? (mode.amount / totalByMode) * 100 : 0;
            return (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <CreditCard className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">{mode.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(mode.amount)}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${mode.color} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Total Transactions</span>
            <span className="text-lg font-bold text-gray-900">
              {stats?.total_transactions || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <h3 className="text-lg font-semibold mb-3">Financial Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="opacity-90 mb-1">Average per Transaction</p>
            <p className="text-xl font-bold">
              {formatCurrency(stats?.total_transactions > 0 ? stats.total_revenue / stats.total_transactions : 0)}
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Collection Rate</p>
            <p className="text-xl font-bold">
              {stats?.total_revenue + stats?.total_pending > 0
                ? ((stats.total_revenue / (stats.total_revenue + stats.total_pending)) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Month Progress</p>
            <p className="text-xl font-bold">
              {new Date().getDate()} / {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()} days
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;